import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError, type User } from '../../api';
import { useMe } from '../../hooks';

export default function Login() {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const state = location.state as { from?: string } | null;
  const from = state?.from && !state.from.startsWith('/login') ? state.from : '/';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (me.data) {
    return <Navigate to={me.data.must_change_password ? '/account/password' : from} replace />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const result = await api<{ user: User }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username: username.trim(), password }),
      });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate(result.user.must_change_password ? '/account/password' : from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_credentials') {
        setError('用户名或密码不正确');
      } else if (err instanceof ApiError && err.code === 'too_many_attempts') {
        setError('尝试次数过多，请一分钟后再试');
      } else {
        setError('登录失败，请稍后再试');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <section className="login-visual">
        <div>
          <img
            className="login-owner"
            src="/brand/lab-logo-light.png"
            alt="碳捕集与能源利用数智实验室"
          />
          <div className="login-badge">
            <i />
            科研数据门户 · 只读回放
          </div>
          <h1>把试验装置，放到每个人的屏幕上。</h1>
          <p>
            CCS 采集门户把工控机上的 P&amp;ID 数据面板搬到浏览器：多人同时查看、按时间回放、不需要启动设备。
          </p>
        </div>
        <div className="login-foot">
          <span>碳捕集与能源利用数智实验室</span>
          <span>CCS 中试装置</span>
          <span>v0.1</span>
        </div>
      </section>

      <section className="login-form-wrap">
        <form className="login-card" onSubmit={submit}>
          <div className="brand">
            <img className="brand-mark" src="/brand/lab-emblem.png" alt="" />
            <div>
              碳捕集与能源利用数智实验室
              <small>CEUS · Digital Smart Lab</small>
            </div>
          </div>
          <h2>登录</h2>
          <p className="sub">使用账号或手机号登录。还没有账号？可自助注册访客账号。</p>

          {error ? <div className="error">{error}</div> : null}

          <div className="field">
            <label htmlFor="username">用户名 / 手机号</label>
            <input
              id="username"
              className="input"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="请输入用户名或手机号"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="password">密码</label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="请输入密码"
              required
            />
          </div>

          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? '登录中…' : '进入门户'}
          </button>

          <div className="login-alt">
            还没有账号？<Link to="/register">申请访客账号</Link>
          </div>

          <div className="login-note">
            公开内容可自助注册访客账号浏览；内部数据与实验控制仅对授权成员开放。所有登录、导入与查看操作会记录审计日志。
          </div>
        </form>
      </section>
    </div>
  );
}